"use client";

import { useEffect, useRef, useState } from "react";
import { useSiteContext } from "@/components/providers/SiteContextProvider";
import { getTranslations } from "@/lib/i18n";

/**
 * Sidebar trip-config block — Reisgezelschap + Vertrekdatum + Aantal nachten +
 * Exacte vertrekdatum, per Dennis Figma (2026-05-20).
 *
 * Controlled component: parent owns state (the values also drive the
 * category-page listing filter via length_of_stay matching, and feed into
 * <DealCard filterParams=…> so the booking page opens with the right party).
 * On change, parent persists to the ft_travel_party cookie + URL.
 */
export default function TripConfigBlock({
  date,
  nights,
  adults,
  childrenCount,
  exactDate,
  onChange,
  variant = "sidebar",
}) {
  const { locale } = useSiteContext();
  const { t } = getTranslations(locale);
  const [partyOpen, setPartyOpen] = useState(false);
  const partyRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (partyRef.current && !partyRef.current.contains(e.target)) {
        setPartyOpen(false);
      }
    }
    if (partyOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [partyOpen]);

  const adultsLabel = adults === 1 ? t("hero_search.adult_one", "volwassene") : t("hero_search.adult_other", "volwassenen");
  const childrenLabel = childrenCount === 1 ? t("hero_search.child_one", "kind") : t("hero_search.child_other", "kinderen");
  const partyLabel = `${adults} ${adultsLabel}${childrenCount > 0 ? `, ${childrenCount} ${childrenLabel}` : ""}`;

  const today = new Date().toISOString().split("T")[0];

  return (
    <div className={variant === "sidebar" ? "pb-3" : ""}>
      <h2 className="text-[15px] font-semibold text-gray-900 mb-2">
        {t("listing.trip_config_heading", "Reisgezelschap")}
      </h2>

      <div className="relative" ref={partyRef}>
        <button
          type="button"
          onClick={() => setPartyOpen((v) => !v)}
          className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-800 hover:border-gray-400"
        >
          <span>{partyLabel}</span>
          <svg className={`w-4 h-4 text-gray-500 transition-transform ${partyOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {partyOpen && (
          <div className="absolute left-0 top-full mt-1 w-full bg-white rounded-md shadow-lg border border-gray-200 p-3 z-30">
            <div className="flex items-center justify-between mb-3 gap-3">
              <label className="text-sm font-medium text-gray-900" htmlFor="tcb-adults">
                {t("hero_search.adults_title", "Volwassenen")}
              </label>
              <select
                id="tcb-adults"
                value={adults}
                onChange={(e) => onChange({ adults: Number(e.target.value) })}
                className="border rounded-md px-2 py-1 text-sm bg-white"
              >
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center justify-between gap-3">
              <label className="text-sm font-medium text-gray-900" htmlFor="tcb-children">
                {t("hero_search.children_title", "Kinderen")}
              </label>
              <select
                id="tcb-children"
                value={childrenCount}
                onChange={(e) => onChange({ children: Number(e.target.value) })}
                className="border rounded-md px-2 py-1 text-sm bg-white"
              >
                {[0, 1, 2, 3, 4].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      <h2 className="text-[15px] font-semibold text-gray-900 mt-4 mb-2">
        {t("listing.trip_departure", "Vertrekdatum")}
      </h2>
      <input
        type="date"
        value={date || ""}
        min={today}
        onChange={(e) => onChange({ date: e.target.value })}
        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
        style={{ colorScheme: "light" }}
      />

      <h2 className="text-[15px] font-semibold text-gray-900 mt-4 mb-2">
        {t("listing.trip_days", "Aantal dagen")}
      </h2>
      {/* Dennis 2026-06-03: always express duration in DAYS, never nights. The
          option value is matched against deal.length_of_stay, which stores the
          trip length in DAYS, so the number IS the day count — only the label
          changes from "nachten" to "dagen". */}
      <select
        value={nights || "any"}
        onChange={(e) => onChange({ nights: e.target.value })}
        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
      >
        <option value="any">{t("listing.trip_days_any", "Alle")}</option>
        <option value="1">{t("listing.trip_days_n", "{n} dag", { n: 1 })}</option>
        <option value="2">{t("listing.trip_days_n_plural", "{n} dagen", { n: 2 })}</option>
        <option value="3">{t("listing.trip_days_n_plural", "{n} dagen", { n: 3 })}</option>
        <option value="4">{t("listing.trip_days_n_plural", "{n} dagen", { n: 4 })}</option>
        <option value="5+">{t("listing.trip_days_long", "5 dagen of meer")}</option>
      </select>

      <label className="flex items-center gap-2 mt-3 cursor-pointer">
        <input
          type="checkbox"
          checked={!!exactDate}
          onChange={(e) => onChange({ exactDate: e.target.checked })}
          className="w-4 h-4 rounded border-gray-300 accent-[#0A8DE9]"
        />
        <span className="text-sm text-gray-700">
          {t("listing.trip_exact_date", "Exacte vertrekdatum")}
        </span>
      </label>
    </div>
  );
}
